import { useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api/endpoints";
import { ApiError, errorMessage } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Field } from "../components/Field";
import { FormError, Spinner } from "../components/States";
import { EyeIcon, EyeSlashIcon, KeyReturnIcon, ShieldCheckIcon, SignOutIcon } from "@phosphor-icons/react";
import { Button } from "../components/Button";
import { TerminalInput } from "../components/TerminalInput";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useTyped } from "../hud/useTyped";
import { Prompt, TerminalScreen } from "./AuthPage";

// Full-screen step after signing in with a temporary password. Nothing else
// loads until it succeeds: the server answers 403 to everything else and
// refuses WebSockets. The new token replaces the old one (which is now dead).

export const MIN_PASSWORD = 10;

export function SetPasswordPage() {
  useDocumentTitle("Set your password");
  const { state, signIn, signOut } = useAuth();
  const name = state.status === "password-required" ? state.user.name : "";
  const email = state.status === "password-required" ? (state.user.email ?? "") : "";
  const org = state.status === "password-required" ? state.site.name : "";
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const typed = useTyped(["border rangers // new passcode", "temporary", "new", "confirm"], 32, 200);
  const [hdr = "", k1 = "", k2 = "", k3 = ""] = typed.shown;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = !current
      ? "enter the temporary passcode you signed in with."
      : next.length < MIN_PASSWORD
        ? `the new passcode needs at least ${MIN_PASSWORD} characters.`
        : next === current
          ? "pick a passcode different from the temporary one."
          : confirmPw !== next
            ? "the two new passcodes do not match."
            : null;
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      signIn(await api.changePassword(current, next));
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? "that temporary passcode is wrong. check it with the owner who gave it to you."
          : errorMessage(err).toLowerCase(),
      );
      setBusy(false);
    }
  };

  return (
    <TerminalScreen header={hdr}>
      <form className="term__form" onSubmit={submit} noValidate aria-labelledby="setpw-title">
        <h1 id="setpw-title" className="sr-only">
          Set your password
        </h1>
        <p className="term__note">
          # welcome{name ? `, ${name.toLowerCase()}` : ""}. {org ? `${org.toLowerCase()} gave you` : "you signed in with"} a temporary passcode. set your own ({MIN_PASSWORD}+ characters) before anything else opens.
        </p>
        {/* Lets password managers file the new passcode under the right account. */}
        <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />
        <TerminalInput prompt={<Prompt text={k1} />} label="Temporary password" masked name="current-password" autoComplete="current-password" autoFocus required value={current} onChange={setCurrent} idleCursor={!current} />
        <TerminalInput prompt={<Prompt text={k2} />} label="New password" masked name="new-password" autoComplete="new-password" minLength={MIN_PASSWORD} required value={next} onChange={setNext} />
        <TerminalInput prompt={<Prompt text={k3} />} label="Confirm new password" masked name="confirm-password" autoComplete="new-password" minLength={MIN_PASSWORD} required value={confirmPw} onChange={setConfirmPw} />
        {error ? (
          <p className="term__err" role="alert">
            ! {error}
          </p>
        ) : null}
        <div className="enter">
          <Button type="submit" variant="primary" icon={KeyReturnIcon} block disabled={busy}>
            {busy ? "saving…" : "set passcode"}
          </Button>
        </div>
        <p className="term__help">
          <Button size="sm" variant="ghost" icon={SignOutIcon} onClick={() => void signOut()}>
            sign out instead
          </Button>
        </p>
      </form>
    </TerminalScreen>
  );
}

export function PasswordInput({
  autoComplete,
  value,
  onChange,
  onBlur,
  props,
  minLength,
  name = "password",
}: {
  autoComplete: "current-password" | "new-password";
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  props: { id: string; "aria-describedby"?: string; "aria-invalid"?: true };
  minLength?: number;
  name?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="password-wrap">
      <input
        {...props}
        className="input"
        name={name}
        type={show ? "text" : "password"}
        autoComplete={autoComplete}
        spellCheck={false}
        autoCapitalize="none"
        required
        minLength={minLength}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      <button type="button" className="btn btn--ghost btn--sm" aria-label={show ? "Hide password" : "Show password"} aria-pressed={show} onClick={() => setShow((v) => !v)}>{show ? <EyeSlashIcon size={16} aria-hidden="true" /> : <EyeIcon size={16} aria-hidden="true" />}
        {show ? "hide" : "show"}
      </button>
    </div>
  );
}

/** Shared by the first-sign-in step and Settings. Calls POST /api/auth/password. */
export function SetPasswordForm({
  temporary,
  onDone,
  submitLabel,
}: {
  temporary?: boolean;
  onDone: (res: Awaited<ReturnType<typeof api.changePassword>>) => void;
  submitLabel: string;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const errs = {
    current: !current ? (temporary ? "Enter the temporary password you signed in with." : "Enter your current password.") : undefined,
    next: next.length < MIN_PASSWORD ? `Use at least ${MIN_PASSWORD} characters.` : next === current ? "Pick a password different from the current one." : undefined,
    confirm: confirmPw !== next ? "The two new passwords do not match." : undefined,
  };
  const show = (k: keyof typeof errs) => (submitted ? errs[k] : undefined);

  useEffect(() => {
    if (error) formRef.current?.querySelector<HTMLElement>(".form-error")?.focus();
  }, [error]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (errs.current || errs.next || errs.confirm) {
      requestAnimationFrame(() => formRef.current?.querySelector<HTMLElement>("[aria-invalid=true]")?.focus());
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.changePassword(current, next);
      onDone(res);
      setCurrent("");
      setNext("");
      setConfirmPw("");
      setSubmitted(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? temporary
            ? "That temporary password is wrong. Check it with the owner who gave it to you."
            : "Your current password is wrong."
          : errorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="form" onSubmit={submit} noValidate ref={formRef}>
      <FormError message={error} />
      <Field label={temporary ? "Temporary password" : "Current password"} error={show("current")}>
        {(p) => <PasswordInput name="current-password" autoComplete="current-password" value={current} onChange={setCurrent} props={p} />}
      </Field>
      <Field label="New password" help={`At least ${MIN_PASSWORD} characters. Changing it signs out your other sessions.`} error={show("next")}>
        {(p) => <PasswordInput name="new-password" autoComplete="new-password" minLength={MIN_PASSWORD} value={next} onChange={setNext} props={p} />}
      </Field>
      <Field label="Confirm new password" error={show("confirm")}>
        {(p) => <PasswordInput name="confirm-password" autoComplete="new-password" minLength={MIN_PASSWORD} value={confirmPw} onChange={setConfirmPw} props={p} />}
      </Field>
      <button type="submit" className="btn btn--primary btn--block" disabled={busy}>
        {busy ? <Spinner /> : <ShieldCheckIcon size={16} aria-hidden="true" />}
        {busy ? "Saving…" : submitLabel}
      </button>
    </form>
  );
}
